import clsx from 'clsx';
import * as React from 'react';
import { type RadioGroupAria, useRadioGroup as useAriaRadioGroup } from 'react-aria';
import {
    type RadioGroupProps as AriaRadioGroupProps,
    type RadioGroupState,
    useRadioGroupState,
} from 'react-stately';

import { useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { useAriaFieldProps } from '../Form';

const RadioGroupContext = React.createContext<RadioGroupState | null>(null);

/**
 * Hook to get the current radio group state from context.
 */
export function useRadioGroupContext() {
    const context = React.useContext(RadioGroupContext);
    assert(context, 'Must be used within a RadioGroup');
    return context;
}

export type RadioGroupRef = {
    focus: () => void;
};

export type RadioGroupHookProps = AriaRadioGroupProps & {
    ref: React.Ref<RadioGroupRef> | undefined;
    children: React.ReactNode;
};

export type RadioGroupHookResult = {
    state: RadioGroupState;
    ref: React.RefCallback<HTMLDivElement>;
    radioGroupProps: RadioGroupAria['radioGroupProps'] & {
        children: React.ReactElement;
    };
};

/**
 * Low-level hook to create a card radio group component.
 */
export function useRadioGroup({
    ref,
    children,
    ...props
}: RadioGroupHookProps): RadioGroupHookResult {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const fieldProps = useAriaFieldProps(props);
    const state = useRadioGroupState(fieldProps);
    const { radioGroupProps } = useAriaRadioGroup(fieldProps, state);
    React.useImperativeHandle(
        ref,
        () => ({
            focus: () => {
                // Focus the first input in the group
                const input = containerRef.current?.querySelector('input');
                input?.focus();
            },
        }),
        []
    );
    const mergedRef = useMergedRefs(containerRef, fieldProps.ref);

    return {
        ref: mergedRef,
        state,
        radioGroupProps: {
            ...radioGroupProps,
            children: (
                <RadioGroupContext.Provider value={state}>{children}</RadioGroupContext.Provider>
            ),
        },
    };
}

export type RadioGroupProps = Omit<RadioGroupHookProps, 'ref'> & {
    className?: string;
    style?: React.CSSProperties;
};
export type RadioGroupPropsWithRef = RadioGroupProps & { ref?: React.Ref<HTMLDivElement> };

/**
 * Radio group component to use a group of radio buttons.
 */
export const RadioGroup = React.forwardRef(function RadioGroup(
    props: RadioGroupProps,
    forwardedRef: React.ForwardedRef<RadioGroupRef>
) {
    const { ref, radioGroupProps } = useRadioGroup({ ...props, ref: forwardedRef });
    return (
        <div
            ref={ref}
            {...radioGroupProps}
            className={clsx('flex gap-3 aria-[orientation=vertical]:flex-col', props.className)}
            style={props.style}
        />
    );
});
